import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDashboardBlockData,
  getDashboardCatalog,
  getDashboardFormatters,
  updateDashboardBlock,
} from '@/api/dashboards';
import { BlockFilters } from './BlockFilters';
import { CHART_COLOR_SCHEME_OPTIONS } from './chart-colors';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import type {
  ChartType,
  Dashboard,
  DashboardBlock,
  DashboardBlockSize,
  DashboardFilterDef,
  DashboardTextLayout,
  DashboardValueMode,
  DashboardVisualization,
} from '@pm-next/shared-types';
import {
  DASHBOARD_CHART_COLOR_SCHEMES,
  DASHBOARD_FORMATTER_IDS,
  gridToSize,
  humanizeLabel,
} from '@pm-next/shared-types';

interface ConfigureBlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboardId: string;
  block: DashboardBlock;
}

export function ConfigureBlockDialog({
  open,
  onOpenChange,
  dashboardId,
  block,
}: ConfigureBlockDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(block.title);
  const [visualization, setVisualization] = useState<DashboardVisualization>(block.visualization);
  const [chartType, setChartType] = useState<ChartType>(block.chartType || 'bar');
  const [size, setSize] = useState<DashboardBlockSize>(gridToSize(block.w));
  const [hideFilters, setHideFilters] = useState(block.config?.ui?.hideFilters === true);
  const [toolArgs, setToolArgs] = useState(block.toolArgs);
  const [filters, setFilters] = useState<DashboardFilterDef[]>(block.filters);
  const [valueMode, setValueMode] = useState<DashboardValueMode>(
    block.config?.display?.valueMode || 'count'
  );
  const [formatterId, setFormatterId] = useState(
    block.config?.display?.formatterId || DASHBOARD_FORMATTER_IDS.number
  );
  const [colorScheme, setColorScheme] = useState(
    block.config?.chart?.colorScheme || DASHBOARD_CHART_COLOR_SCHEMES.default
  );
  const [showLabels, setShowLabels] = useState(block.config?.chart?.showLabels === true);
  const [showLegend, setShowLegend] = useState(block.config?.chart?.showLegend !== false);
  const [textLayout, setTextLayout] = useState<DashboardTextLayout>(
    block.config?.text?.layout === 'list' ? 'list' : 'cards'
  );
  const [seriesKeys, setSeriesKeys] = useState<string[]>(block.config?.chart?.seriesKeys ?? []);
  const [negativeSeriesKeys, setNegativeSeriesKeys] = useState<string[]>(
    block.config?.chart?.negativeSeriesKeys ?? []
  );
  const [showTotalLine, setShowTotalLine] = useState(block.config?.chart?.showTotalLine === true);

  const { data: catalog = [] } = useQuery({
    queryKey: ['dashboard-catalog'],
    queryFn: getDashboardCatalog,
    enabled: open,
  });

  const { data: formatters = [] } = useQuery({
    queryKey: ['dashboard-formatters'],
    queryFn: getDashboardFormatters,
    enabled: open,
  });

  const { data: blockData } = useQuery({
    queryKey: ['dashboard-block-data', dashboardId, block.id, 'configure-series'],
    queryFn: () => getDashboardBlockData(dashboardId, block.id, { page: 1, pageSize: 1 }),
    enabled: open && visualization === 'chart',
  });

  const entry = catalog.find((c) => c.toolName === block.toolName);

  const availableFilters = useMemo(() => {
    if (filters.length > 0) return filters;
    return entry?.filters ?? [];
  }, [filters, entry]);

  const valueModes = entry?.valueModes?.length ? entry.valueModes : [];
  const formatterOptions = useMemo(() => {
    const suggested = entry?.suggestedFormatterIds;
    if (suggested?.length) {
      const preferred = formatters.filter((f) => suggested.includes(f.id));
      if (preferred.length > 0) return preferred;
    }
    return formatters;
  }, [entry, formatters]);

  const availableSeries = useMemo(() => {
    const fromData = blockData?.chart?.availableSeries;
    if (fromData?.length) return fromData;
    return (entry?.defaultSeriesKeys ?? []).map((key) => ({
      key,
      label: key,
    }));
  }, [blockData, entry]);

  useEffect(() => {
    if (!open) return;
    setTitle(block.title);
    setVisualization(block.visualization);
    setChartType(block.chartType || 'bar');
    setSize(gridToSize(block.w));
    setHideFilters(block.config?.ui?.hideFilters === true);
    setToolArgs(block.toolArgs);
    setFilters(block.filters.length > 0 ? block.filters : (entry?.filters ?? []));
    setValueMode(block.config?.display?.valueMode || entry?.defaultValueMode || 'count');
    setFormatterId(
      block.config?.display?.formatterId ||
        entry?.defaultFormatterId ||
        DASHBOARD_FORMATTER_IDS.number
    );
    setColorScheme(block.config?.chart?.colorScheme || DASHBOARD_CHART_COLOR_SCHEMES.default);
    setShowLabels(block.config?.chart?.showLabels === true);
    setShowLegend(block.config?.chart?.showLegend !== false);
    setTextLayout(block.config?.text?.layout === 'list' ? 'list' : 'cards');
    setSeriesKeys(block.config?.chart?.seriesKeys ?? entry?.defaultSeriesKeys ?? []);
    setNegativeSeriesKeys(block.config?.chart?.negativeSeriesKeys ?? []);
    setShowTotalLine(block.config?.chart?.showTotalLine === true);
  }, [open, block, entry]);

  useEffect(() => {
    if (!open || visualization !== 'chart') return;
    if (block.config?.chart?.seriesKeys?.length) return;
    if (seriesKeys.length > 0) return;
    const active = blockData?.chart?.series;
    if (active?.length) setSeriesKeys(active);
  }, [
    open,
    visualization,
    block.config?.chart?.seriesKeys,
    blockData?.chart?.series,
    seriesKeys.length,
  ]);

  const save = useMutation({
    mutationFn: () =>
      updateDashboardBlock(dashboardId, block.id, {
        title: title.trim() || block.title,
        visualization,
        chartType: visualization === 'chart' ? chartType : null,
        size,
        toolArgs,
        filters: availableFilters,
        config: {
          version: 1,
          display: {
            valueMode: valueModes.length > 0 ? valueMode : undefined,
            formatterId,
          },
          ui: { hideFilters },
          chart:
            visualization === 'chart'
              ? {
                  colorScheme,
                  showLabels,
                  showLegend,
                  seriesKeys: seriesKeys.length > 0 ? seriesKeys : undefined,
                  negativeSeriesKeys:
                    chartType === 'stack'
                      ? negativeSeriesKeys.filter((k) => seriesKeys.includes(k))
                      : [],
                  showTotalLine: chartType === 'stack' ? showTotalLine : false,
                }
              : undefined,
          text: visualization === 'text' ? { layout: textLayout } : undefined,
        },
      }),
    onSuccess: (updatedBlock) => {
      queryClient.setQueryData<Dashboard>(['dashboard', dashboardId], (current) => {
        if (!current) return current;
        return {
          ...current,
          blocks: current.blocks?.map((item) =>
            item.id === updatedBlock.id ? updatedBlock : item
          ),
        };
      });
      onOpenChange(false);
      toast({ title: 'Block updated' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Update failed', description: err.message });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure block</DialogTitle>
          <DialogDescription>
            Update visualization, display, chart options, and filters. All options are stored in one
            config JSON.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Visualization</Label>
              <Select
                value={visualization}
                onValueChange={(v) => setVisualization(v as DashboardVisualization)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(entry?.allowedVisualizations || ['chart', 'list', 'text']).map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Size</Label>
              <Select value={size} onValueChange={(v) => setSize(v as DashboardBlockSize)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['sm', 'md', 'lg', 'full'] as DashboardBlockSize[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {visualization === 'chart' && (
            <>
              <div className="space-y-1.5">
                <Label>Chart type</Label>
                <Select value={chartType} onValueChange={(v) => setChartType(v as ChartType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['bar', 'line', 'pie', 'donut', 'area', 'stack'] as ChartType[]).map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 rounded-md border p-3">
                <Label className="text-sm font-medium">Chart options</Label>
                {availableSeries.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Data series</Label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {availableSeries.map((s) => {
                        const checked = seriesKeys.includes(s.key);
                        return (
                          <label key={s.key} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                setSeriesKeys((prev) => {
                                  if (v === true) return [...prev, s.key];
                                  return prev.filter((k) => k !== s.key);
                                });
                                if (v !== true) {
                                  setNegativeSeriesKeys((prev) => prev.filter((k) => k !== s.key));
                                }
                              }}
                            />
                            {s.label}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Leave empty to auto-pick up to 4 metrics. Line/bar/area/stack can show
                      multiple series.
                    </p>
                  </div>
                )}
                {chartType === 'stack' && seriesKeys.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Show as negative</Label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {seriesKeys.map((key) => {
                        const label =
                          availableSeries.find((s) => s.key === key)?.label ?? humanizeLabel(key);
                        const checked = negativeSeriesKeys.includes(key);
                        return (
                          <label key={key} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                setNegativeSeriesKeys((prev) => {
                                  if (v === true) return [...prev, key];
                                  return prev.filter((k) => k !== key);
                                });
                              }}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Selected series are inverted so they stack below zero.
                    </p>
                  </div>
                )}
                {chartType === 'stack' && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={showTotalLine}
                      onCheckedChange={(v) => setShowTotalLine(v === true)}
                    />
                    Show total line
                  </label>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Color scheme</Label>
                  <Select value={colorScheme} onValueChange={setColorScheme}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHART_COLOR_SCHEME_OPTIONS.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={showLabels}
                    onCheckedChange={(v) => setShowLabels(v === true)}
                  />
                  Display labels on chart
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={showLegend}
                    onCheckedChange={(v) => setShowLegend(v === true)}
                  />
                  Show legend
                </label>
              </div>
            </>
          )}

          {visualization === 'text' && (
            <div className="space-y-1.5">
              <Label>Text layout</Label>
              <Select
                value={textLayout}
                onValueChange={(v) => setTextLayout(v as DashboardTextLayout)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cards">Cards</SelectItem>
                  <SelectItem value="list">Key / value list</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                List mode shows metrics as two columns (key and value).
              </p>
            </div>
          )}

          {(valueModes.length > 1 || formatterOptions.length > 0) && (
            <div className="grid grid-cols-2 gap-3">
              {valueModes.length > 1 && (
                <div className="space-y-1.5">
                  <Label>Value</Label>
                  <Select
                    value={valueMode}
                    onValueChange={(v) => {
                      const next = v as DashboardValueMode;
                      setValueMode(next);
                      if (next === 'percentage' && !formatterId.includes('percent')) {
                        setFormatterId(DASHBOARD_FORMATTER_IDS.percent);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {valueModes.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m === 'percentage' ? 'Percentage' : m === 'auto' ? 'Auto' : 'Count'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formatterOptions.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Format</Label>
                  <Select value={formatterId} onValueChange={setFormatterId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {formatterOptions.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.label}
                          {f.sample ? ` (${f.sample})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2 rounded-md border p-3">
            <Label className="text-sm font-medium">Filters</Label>
            {availableFilters.length > 0 ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Default values</Label>
                  <BlockFilters
                    filters={availableFilters}
                    toolArgs={toolArgs}
                    onChange={setToolArgs}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!hideFilters}
                    onCheckedChange={(v) => setHideFilters(v !== true)}
                  />
                  Show filters on dashboard
                </label>
                <p className="text-xs text-muted-foreground">
                  When off, filters stay hidden in view mode but saved defaults still apply. They
                  remain available here and in edit mode.
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                This data source has no configurable filters.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
